/**
 * The /account premium panel. It renders from an explicit server-derived
 * billing view model (`GET /api/stripe/accountState`) rather than inferring
 * state from the mere presence of a Stripe customer — see
 * docs/todos/tasks/stripeRefactor.md.
 *
 * Readlang copy never presents a bridge (deferred billing) as a free trial or
 * as complimentary access: it always says "billing starts {date}".
 */
import * as preact from "preact";

import * as appStorePurchases from "appStorePurchases";
import * as deviceDetect from "deviceDetect";
import * as genericModals from "genericModals";
import { postNavigate } from "postNavigate";
import readlangClient from "readlangClient";
import { COUNTRIES } from "shared/countries";
import * as subscriptionPlans from "shared/subscriptionPlans";
import * as utils from "shared/utils";
import stores from "stores/stores";

interface Props {}

interface BillingAddress {
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  country: string;
}

interface BillingInfo {
  card: { funding: string; last4: string } | null;
  address: BillingAddress | null;
  billingState: "addressNeeded" | "addressReady" | "refused" | "synced" | null;
}

type Tier = "standard" | "plus";

interface BridgeOption {
  plan: Tier;
  interval: "month" | "year";
  priceID: string;
  priceDescription: string;
  billingStartsAt: string;
}

type AccountPremiumState =
  | {
      type: "accessGrant";
      plan: Tier;
      endsAt: string;
      sourceLabel: string;
      renewsAutomatically: false;
      bridgeOptions: BridgeOption[];
    }
  | {
      type: "stripeBridge";
      plan: Tier;
      billingStartsAt: string;
      cancelAtPeriodEnd: boolean;
      priceID: string;
      priceDescription?: string;
      // Server-decided, by the same rule the plan-change routes enforce: the
      // page never offers a change they would refuse (see
      // accountPremiumState.planChangeEligibility).
      canChangePlan: boolean;
      planChangeUnavailableMessage?: string;
    }
  | {
      type: "stripeSubscription";
      plan: Tier;
      currentPeriodEnd: string;
      cancelAtPeriodEnd: boolean;
      status: string;
      priceID: string;
      priceDescription?: string;
      // Current billing cadence (1 or 12), server-derived so grandfathered
      // Prices the client can't map still get a cadence.
      months?: number;
      canChangePlan: boolean;
      planChangeUnavailableMessage?: string;
    }
  | {
      // Bought in the iOS app: Apple bills it, and the user manages it in iOS
      type: "appStoreSubscription";
      plan: Tier;
      expiresAt: string;
      renewsAutomatically: boolean;
      months?: number;
    }
  | { type: "freeForever"; plan: "plus" }
  | { type: "free"; options: unknown[] };

/**
 * One preview shape for all three states: a plan change converts remaining
 * value into time at the new plan and never moves money, so there is only ever
 * a date to show — and nothing to sign or re-verify when it is applied.
 */
interface PlanChangePreview {
  type: "accessGrant" | "stripeBridge" | "stripeSubscription";
  plan: Tier;
  priceDescription?: string;
  previousDate: string;
  newDate: string;
  // The converted date is further ahead than Stripe can schedule automatic
  // renewal, so confirming ends the subscription and keeps the access as a
  // non-renewing grant instead. The user is told before confirming.
  overCap: boolean;
}

/** The two states backed by a Stripe Subscription: a bridge, or a paid one. */
type SubscriptionBackedState = Extract<
  AccountPremiumState,
  { type: "stripeBridge" | "stripeSubscription" }
>;

type State =
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "loaded";
      accountState: AccountPremiumState;
      cardString: string | undefined;
      billingInfo: BillingInfo;
      // Form state
      streetLine1: string;
      streetLine2: string;
      city: string;
      postalCode: string;
      country: string;
      submitting: boolean;
      addressSubmitted: boolean;
      showAddressForm: boolean;
    };

/** The plan names offered for a change, in the order the account page lists them. */
const SELECTABLE_PLANS: Array<{ tier: Tier; months: 1 | 12 }> = [
  { tier: "standard", months: 1 },
  { tier: "standard", months: 12 },
  { tier: "plus", months: 1 },
  { tier: "plus", months: 12 },
];

const tierName = (tier: Tier) => (tier === "plus" ? "Premium Plus" : "Premium");

/**
 * A subscription whose renewal payment has failed and is still in Stripe's
 * dunning. Its current period is the one it has NOT paid for, so no date it
 * carries may be presented as access bought or as a payment falling due.
 */
const dunning = (accountState: SubscriptionBackedState) =>
  accountState.type === "stripeSubscription" &&
  (accountState.status === "past_due" || accountState.status === "unpaid");

const formatDate = (isoDate: string) =>
  new Date(isoDate).toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

/**
 * The Price a plan option corresponds to. A grant's plan change only cares
 * about the tier (granted time has no billing cadence), so it sends the
 * monthly Price of the target tier and the server reads its permissions.
 */
const priceIDFor = (tier: Tier, months: 1 | 12) =>
  subscriptionPlans.getDefaultPlan(tier, months).name;

class ManageSubscriptionView extends preact.Component<Props, State> {
  constructor() {
    super();
    this.state = { status: "loading" };
    this.load();
  }

  load() {
    // The billing view model and the payment method are independent; the view
    // model is what decides which state renders, so a billing-info failure
    // must not blank the page.
    readlangClient.request(
      "GET",
      "/api/stripe/accountState",
      {},
      (accountState: AccountPremiumState) => {
        this.loadBillingInfo(accountState);
      },
      () => {
        this.setState({ status: "error" });
      }
    );
  }

  loadBillingInfo(accountState: AccountPremiumState) {
    const loaded = (billingInfo: BillingInfo) => {
      const cardString = billingInfo.card
        ? `${utils.toTitleCase(billingInfo.card.funding)} card, ends with ${billingInfo.card.last4}`
        : undefined;

      this.setState({
        status: "loaded",
        accountState,
        cardString,
        billingInfo,
        // Pre-populate form with existing address if available
        streetLine1: billingInfo.address?.line1 || "",
        streetLine2: billingInfo.address?.line2 || "",
        city: billingInfo.address?.city || "",
        postalCode: billingInfo.address?.postalCode || "",
        country: billingInfo.address?.country || "",
        submitting: false,
        addressSubmitted: false,
        showAddressForm: false,
      });

      // Scroll to billing section if URL hash is present
      if (window.location.hash === "#billing") {
        setTimeout(() => {
          document
            .getElementById("billing")
            ?.scrollIntoView({ behavior: "smooth" });
        }, 0);
      }
    };

    readlangClient.request(
      "GET",
      "/api/stripe/billingInfo",
      {},
      loaded,
      (error) => {
        if (error?.alreadyNotified) {
          // Terminal failure (network loss, 5xx): don't misreport the outage
          // as "no payment method" — show an error state instead.
          this.setState({ status: "error" });
          return;
        }
        // On a domain error, set empty billing info (e.g. no Stripe customer)
        loaded({ card: null, address: null, billingState: null });
      }
    );
  }

  submitAddress = () => {
    if (this.state.status !== "loaded") return;

    const { streetLine1, postalCode, country } = this.state;
    if (!streetLine1 || !postalCode || !country) {
      alert(
        "Please fill in all required fields (Street, Postal Code, Country)"
      );
      return;
    }

    this.setState({ submitting: true });

    readlangClient.request(
      "POST",
      "/api/stripe/billing/address",
      {
        streetLine1: this.state.streetLine1,
        streetLine2: this.state.streetLine2,
        city: this.state.city,
        postalCode: this.state.postalCode,
        country: this.state.country,
      },
      () => {
        if (this.state.status !== "loaded") return;
        // Hide the notification bar in mainLayout
        stores.user.patch({ billingAddressNeeded: false });
        this.setState({
          submitting: false,
          addressSubmitted: true,
          showAddressForm: false,
          billingInfo: {
            ...this.state.billingInfo,
            billingState: "addressReady",
            address: {
              line1: this.state.streetLine1,
              line2: this.state.streetLine2,
              city: this.state.city,
              postalCode: this.state.postalCode,
              country: this.state.country,
            },
          },
        });
      },
      (error) => {
        this.setState({ submitting: false });
        if (!error.alreadyNotified) {
          alert("There was an error saving your address. Please try again.");
        }
      }
    );
  };

  refuseAddress = () => {
    if (this.state.status !== "loaded") return;

    // Country is required even for refusals
    if (!this.state.country) {
      alert(
        "Please select your country before declining to provide an address."
      );
      return;
    }

    genericModals.confirm({
      title: "Decline to Provide Address?",
      message:
        'If you decline, your invoices will show "Address not provided" in Spanish. ' +
        "If too many people do this, Readlang could get into trouble with the Spanish tax " +
        "authorities and in that case this option will be removed.\n\n" +
        "Are you sure you want to proceed?",
      callback: (response) => {
        if (response !== "ok") return;

        if (this.state.status !== "loaded") return;
        this.setState({ submitting: true });

        readlangClient.request(
          "POST",
          "/api/stripe/billing/refuse",
          { country: this.state.country },
          () => {
            if (this.state.status !== "loaded") return;
            // Hide the notification bar in mainLayout
            stores.user.patch({ billingAddressNeeded: false });
            this.setState({
              submitting: false,
              billingInfo: {
                ...this.state.billingInfo,
                billingState: "refused",
              },
            });
          },
          (error) => {
            this.setState({ submitting: false });
            if (!error.alreadyNotified) {
              alert("There was an error. Please try again.");
            }
          }
        );
      },
    });
  };

  /**
   * Every plan change previews its exact outcome on the server first, then
   * applies it only after the user confirms that preview.
   */
  changePlan = (priceID: string) => {
    readlangClient.request(
      "GET",
      "/api/stripe/planChangePreview",
      { plan: priceID },
      (preview: PlanChangePreview) => {
        genericModals.confirm({
          title: `Change to ${tierName(preview.plan)}?`,
          message: this.planChangeMessage(preview),
          callback: (response) => {
            if (response !== "ok") {
              this.forceUpdate();
              return;
            }
            this.applyPlanChange(priceID);
          },
        });
      },
      (error) => {
        if (!error.alreadyNotified) {
          alert(
            "Couldn't work out what this change would do. Please contact steve@readlang.com."
          );
        }
      }
    );
  };

  planChangeMessage(preview: PlanChangePreview) {
    if (preview.type === "accessGrant") {
      return (
        `Changing to ${tierName(preview.plan)} will change your access end ` +
        `date from ${formatDate(preview.previousDate)} to ` +
        `${formatDate(preview.newDate)}. No payment will be taken.`
      );
    }

    if (preview.overCap) {
      // Only a large Premium Plus -> Premium change reaches here: the
      // converted date is further ahead than Stripe can hold auto-renewal, so
      // the subscription ends and the time becomes non-renewing access.
      return (
        `Changing to ${tierName(preview.plan)} would move your next payment ` +
        `to ${formatDate(preview.newDate)}. That's further ahead than we can ` +
        "schedule automatic renewal, so your subscription will end and your " +
        "access will continue until then. We'll email you when it ends so " +
        "you can renew."
      );
    }

    if (formatDate(preview.previousDate) === formatDate(preview.newDate)) {
      // A same-tier cadence switch converts with factor 1: the Price changes,
      // the billing date does not, so "from X to X" would read as a bug.
      return (
        `Your next payment stays on ${formatDate(preview.newDate)}. From then ` +
        `you'll pay ${preview.priceDescription}. No payment is taken today.`
      );
    }

    return (
      `Changing to ${tierName(preview.plan)} will move your next payment ` +
      `from ${formatDate(preview.previousDate)} to ` +
      `${formatDate(preview.newDate)}. From then you'll pay ` +
      `${preview.priceDescription}. No payment is taken today.`
    );
  }

  applyPlanChange(priceID: string) {
    // Nothing binds the preview to this request: no money moves either way, so
    // a preview that went stale can only produce a slightly different date.
    readlangClient.request(
      "POST",
      "/api/stripe/planChange",
      { plan: priceID },
      () => window.location.reload(),
      (error) => {
        if (!error.alreadyNotified) {
          alert(
            error?.data?.errorMessage ??
              "There was an error updating your subscription. Please contact steve@readlang.com."
          );
        }
      }
    );
  }

  /**
   * What cancelling actually does, in each state the button is offered from.
   * A subscription in dunning has no paid period left to run out — its current
   * period is the one whose invoice failed — so the server cancels it outright
   * and stops collection, and the confirmation says that instead of promising
   * access "until" a date the user has not paid for.
   */
  cancelConfirmation(accountState: SubscriptionBackedState) {
    if (dunning(accountState)) {
      return {
        title: "Cancel your subscription?",
        message:
          "Your last payment didn't go through. Cancelling ends your " +
          "subscription and your premium access now, and we'll stop trying " +
          "to collect that payment — you won't be charged again.",
      };
    }
    return {
      title: "Turn off auto-renewal?",
      message:
        accountState.type === "stripeBridge"
          ? "Your premium access will continue until " +
            `${formatDate(accountState.billingStartsAt)} and no payment will ` +
            "be taken."
          : "Your premium access will continue until " +
            `${formatDate(accountState.currentPeriodEnd)}, and you won't be ` +
            "charged again.",
    };
  }

  cancelSubscription = (accountState: SubscriptionBackedState) => {
    genericModals.confirm({
      ...this.cancelConfirmation(accountState),
      callback: (response) => {
        if (response !== "ok") {
          this.forceUpdate();
          return;
        }
        readlangClient.request("DELETE", "/api/stripe/subscription", {}, () =>
          window.location.reload()
        );
      },
    });
  };

  resumeSubscription = () => {
    readlangClient.request(
      "POST",
      "/api/stripe/subscription/resume",
      {},
      () => window.location.reload(),
      (error) => {
        if (!error.alreadyNotified) {
          alert(
            "There was an error resuming your subscription. Please contact steve@readlang.com."
          );
        }
      }
    );
  };

  startBridgeCheckout(option: BridgeOption) {
    // The same single grant-aware Checkout route every purchase uses: an
    // active grant makes it a bridge whose billing starts when the granted
    // time ends.
    postNavigate(
      `/api/createCheckoutSession/${option.interval}?plan=` +
        `${option.plan === "plus" ? "premiumPlus" : "premium"}` +
        `&returnURL=${encodeURIComponent(window.location.href)}`
    );
  }

  /**
   * The Plan row for a Subscription-backed state. When the server says this
   * subscription can't change plan right now — it is in dunning, or set to
   * cancel at period end, where every preview would promise a payment that
   * will never happen — the control is replaced by the current plan and the
   * server's own explanation, rather than a select whose every outcome the
   * server would refuse.
   */
  renderPlanRow(accountState: SubscriptionBackedState) {
    return (
      <tr>
        <th>Plan</th>
        <td>
          {accountState.canChangePlan ? (
            this.renderPlanSelect(
              accountState.plan,
              accountState.priceID,
              accountState.type === "stripeSubscription"
                ? accountState.months
                : undefined
            )
          ) : (
            <>
              {tierName(accountState.plan)}
              {accountState.priceDescription
                ? ` (${accountState.priceDescription})`
                : null}
              <br />
              {accountState.planChangeUnavailableMessage}
            </>
          )}
        </td>
      </tr>
    );
  }

  renderPlanSelect(
    currentPlan: Tier,
    currentPriceID?: string,
    currentMonths?: number
  ) {
    // For an active paid subscription (currentMonths given), a tier switch
    // keeps the billing cadence and a cadence switch keeps the tier — the two
    // are separate operations, so the diagonal (both at once) is never
    // offered. A bridge (no currentMonths) offers every selectable Price:
    // during the trial there is no billing anchor to reset.
    const offeredPlans =
      currentMonths === undefined
        ? SELECTABLE_PLANS
        : SELECTABLE_PLANS.filter(
            ({ tier, months }) =>
              months === currentMonths || tier === currentPlan
          );
    return (
      <select
        value={currentPriceID ?? priceIDFor(currentPlan, 1)}
        onChange={(event) => {
          event.preventDefault();
          event.stopPropagation();
          this.changePlan(event.currentTarget.value);
        }}
      >
        {currentPriceID &&
        !SELECTABLE_PLANS.some(
          (option) => priceIDFor(option.tier, option.months) === currentPriceID
        ) ? (
          // A grandfathered Price stays on the subscription that has it and is
          // never newly assignable, so it only appears as the current value.
          <option value={currentPriceID}>
            {tierName(currentPlan)} (your current plan)
          </option>
        ) : null}
        {offeredPlans.map(({ tier, months }) => {
          const plan = subscriptionPlans.getDefaultPlan(tier, months);
          return (
            <option value={plan.name}>
              {tierName(tier)} (${plan.price} /{" "}
              {months === 1 ? "month" : "year"})
            </option>
          );
        })}
      </select>
    );
  }

  /** Grant-only plan choice: granted time has no billing cadence. */
  renderGrantPlanSelect(currentPlan: Tier) {
    return (
      <select
        value={priceIDFor(currentPlan, 1)}
        onChange={(event) => {
          event.preventDefault();
          event.stopPropagation();
          this.changePlan(event.currentTarget.value);
        }}
      >
        {(["standard", "plus"] as Tier[]).map((tier) => (
          <option value={priceIDFor(tier, 1)}>{tierName(tier)}</option>
        ))}
      </select>
    );
  }

  renderPaymentMethodRow() {
    if (this.state.status !== "loaded") return null;
    return (
      <tr>
        <th>Payment Method</th>
        <td>
          {this.state.cardString ?? "No payment method"}
          <br />
          {/* Always offered: a subscriber whose card is missing is exactly the
              one who needs to add one (a past_due retry, or a bridge's first
              real charge). */}
          <button
            onClick={() =>
              postNavigate("/api/stripe/createCheckoutPaymentMethodSession")
            }
          >
            {this.state.cardString
              ? "Change Payment Method"
              : "Add Payment Method"}
          </button>
        </td>
      </tr>
    );
  }

  renderInvoicesRow() {
    return (
      <tr>
        <th>Invoices</th>
        <td>
          <a
            // @ts-ignore
            href={`https://quadernoapp.com/billing/?pk=${__QUADERNO_API_PUBLIC_KEY}`}
            target="_blank"
          >
            View Invoices
          </a>
        </td>
      </tr>
    );
  }

  /**
   * Granted or prepaid access: no payment method, no next payment, no cancel
   * control and no billing-address requirement, and a retained Stripe
   * customer must not make any of them appear.
   */
  renderAccessGrant(
    accountState: Extract<AccountPremiumState, { type: "accessGrant" }>
  ) {
    return (
      <div>
        <h2>Your Premium access</h2>
        <table class="table">
          <caption>
            If you have any questions about your access, please contact{" "}
            <a href="mailto:steve@readlang.com">steve@readlang.com</a>.
          </caption>
          <tr>
            <th>Plan</th>
            <td>{this.renderGrantPlanSelect(accountState.plan)}</td>
          </tr>
          <tr>
            <th>Source</th>
            <td>{accountState.sourceLabel}</td>
          </tr>
          <tr>
            <th>Access available until</th>
            <td>{formatDate(accountState.endsAt)}</td>
          </tr>
          <tr>
            <th>Renewal</th>
            <td>This access does not renew automatically.</td>
          </tr>
        </table>

        {accountState.bridgeOptions.length > 0 ? (
          <div class="bridgeOptions">
            <h3>Keep going after {formatDate(accountState.endsAt)}</h3>
            <p>
              Start a subscription now and nothing is charged until your current
              access ends.
            </p>
            {accountState.bridgeOptions.map((option) => (
              <button
                class="payButton"
                onClick={() => this.startBridgeCheckout(option)}
              >
                {tierName(option.plan)}, {option.priceDescription} — no charge
                until {formatDate(option.billingStartsAt)}
              </button>
            ))}
          </div>
        ) : (
          <p>
            We'll email you a link to subscribe once your access ends on{" "}
            {formatDate(accountState.endsAt)}.
          </p>
        )}
      </div>
    );
  }

  /**
   * A bridge: premium access whose billing starts on a known date. Never
   * described as a trial or as complimentary access.
   */
  renderBridge(
    accountState: Extract<AccountPremiumState, { type: "stripeBridge" }>
  ) {
    return (
      <div>
        <h2>Manage Your {tierName(accountState.plan)} Subscription</h2>
        <table class="table">
          <caption>
            If you have any questions about your subscription, please contact{" "}
            <a href="mailto:steve@readlang.com">steve@readlang.com</a>.
          </caption>
          {this.renderPlanRow(accountState)}
          <tr>
            {/* A cancelling bridge's date is when its access ends, not when
                billing starts — no payment will ever be taken on it. */}
            <th>
              {accountState.cancelAtPeriodEnd
                ? "Access until"
                : "Billing starts"}
            </th>
            <td>
              {formatDate(accountState.billingStartsAt)}
              {accountState.priceDescription && !accountState.cancelAtPeriodEnd
                ? ` at ${accountState.priceDescription}`
                : null}
            </td>
          </tr>
          {this.renderPaymentMethodRow()}
          <tr>
            <th></th>
            <td>
              {accountState.cancelAtPeriodEnd ? (
                <>
                  You won't be charged.
                  <br />
                  <button onClick={this.resumeSubscription}>
                    Resume Subscription
                  </button>
                </>
              ) : (
                <button onClick={() => this.cancelSubscription(accountState)}>
                  Cancel Subscription
                </button>
              )}
            </td>
          </tr>
          {this.renderInvoicesRow()}
          {this.renderBillingAddressRow()}
        </table>

        {this.renderAddressForm()}
      </div>
    );
  }

  renderSubscription(
    accountState: Extract<AccountPremiumState, { type: "stripeSubscription" }>
  ) {
    return (
      <div>
        <h2>Manage Your {tierName(accountState.plan)} Subscription</h2>
        <table class="table">
          <caption>
            If you have any questions about your subscription, please contact{" "}
            <a href="mailto:steve@readlang.com">steve@readlang.com</a>.
          </caption>
          {this.renderPlanRow(accountState)}
          {this.renderPaymentMethodRow()}
          <tr>
            {/* A cancelling subscription has no payment left to schedule, and
                a dunning one's date is the end of the period it has paid for —
                the failed renewal is what the Payment row below explains — so
                neither may be labelled as a payment falling due. */}
            <th>
              {accountState.cancelAtPeriodEnd || dunning(accountState)
                ? "Access until"
                : "Next Payment Due"}
            </th>
            <td>
              {formatDate(accountState.currentPeriodEnd)}
              {accountState.cancelAtPeriodEnd ||
              dunning(accountState) ||
              !accountState.priceDescription
                ? null
                : ` (${accountState.priceDescription})`}
            </td>
          </tr>
          {dunning(accountState) ? (
            <tr>
              <th>Payment</th>
              <td>
                Your last payment didn't go through. We'll retry it — updating
                your payment method above is the quickest fix.
              </td>
            </tr>
          ) : null}
          <tr>
            <th></th>
            <td>
              {accountState.cancelAtPeriodEnd ? (
                <button onClick={this.resumeSubscription}>
                  Resume Subscription
                </button>
              ) : (
                <button onClick={() => this.cancelSubscription(accountState)}>
                  Cancel Subscription
                </button>
              )}
            </td>
          </tr>
          {this.renderInvoicesRow()}
          {this.renderBillingAddressRow()}
        </table>

        {this.renderAddressForm()}
      </div>
    );
  }

  renderBillingAddressRow() {
    if (this.state.status !== "loaded") return null;

    const { billingInfo, showAddressForm } = this.state;

    // Hide when editing the address
    if (showAddressForm) return null;

    // Only show if we have an address (addressReady or synced state)
    if (
      billingInfo.billingState !== "addressReady" &&
      billingInfo.billingState !== "synced"
    ) {
      return null;
    }

    const address = billingInfo.address;
    if (!address) return null;

    // Build address lines from populated fields
    const addressLines: string[] = [];
    if (address.line1) addressLines.push(address.line1);
    if (address.line2) addressLines.push(address.line2);
    if (address.city) addressLines.push(address.city);
    if (address.postalCode) addressLines.push(address.postalCode);
    if (address.country) {
      const country = COUNTRIES.find((c) => c.code === address.country);
      addressLines.push(country ? country.name : address.country);
    }

    return (
      <tr>
        <th>Billing Address</th>
        <td>
          {addressLines.map((line, index) => (
            <div key={index}>{line}</div>
          ))}
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              this.setState({ showAddressForm: true });
            }}
          >
            Edit address
          </a>
        </td>
      </tr>
    );
  }

  renderAddressForm() {
    if (this.state.status !== "loaded") return null;

    const { billingInfo, submitting, addressSubmitted, showAddressForm } =
      this.state;

    // Show form if address is needed OR user clicked to provide after refusing
    const shouldShowForm =
      billingInfo.billingState === "addressNeeded" || showAddressForm;

    if (!shouldShowForm) {
      if (billingInfo.billingState === "refused") {
        return (
          <div class="billingAddressSection" id="billing">
            <p class="refusedNotice">
              You have declined to provide billing information. Your invoices
              will note this in Spanish.
            </p>
            <a
              href="#"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                this.setState({ showAddressForm: true });
              }}
            >
              I'd like to provide my address
            </a>
          </div>
        );
      }
      if (addressSubmitted || billingInfo.billingState === "addressReady") {
        return (
          <div class="billingAddressSection" id="billing">
            <p class="successNotice">
              Thank you! Your billing address has been saved.
            </p>
          </div>
        );
      }
      return null;
    }

    // Determine if we're editing an existing address
    const isEditing =
      billingInfo.billingState === "addressReady" ||
      billingInfo.billingState === "synced";

    return (
      <div class="billingAddressSection" id="billing">
        <h3>
          {isEditing ? "Edit Billing Address" : "Billing Address Required"}
        </h3>
        <p class="explanation">
          To comply with tax regulations as a Spanish company, Readlang needs
          your billing address for invoicing purposes.
        </p>

        <div class="addressForm">
          <div class="formRow">
            <label>
              Street Line 1 <span class="required">*</span>
              <input
                type="text"
                value={this.state.streetLine1}
                onInput={(event) =>
                  this.setState({ streetLine1: event.currentTarget.value })
                }
                placeholder="123 Main Street"
                disabled={submitting}
              />
            </label>
          </div>

          <div class="formRow">
            <label>
              Street Line 2
              <input
                type="text"
                value={this.state.streetLine2}
                onInput={(event) =>
                  this.setState({ streetLine2: event.currentTarget.value })
                }
                placeholder="Apt 4B (optional)"
                disabled={submitting}
              />
            </label>
          </div>

          <div class="formRow">
            <label>
              City
              <input
                type="text"
                value={this.state.city}
                onInput={(event) =>
                  this.setState({ city: event.currentTarget.value })
                }
                placeholder="City (optional)"
                disabled={submitting}
              />
            </label>
          </div>

          <div class="formRow">
            <label>
              Postal Code <span class="required">*</span>
              <input
                type="text"
                value={this.state.postalCode}
                onInput={(event) =>
                  this.setState({ postalCode: event.currentTarget.value })
                }
                placeholder="12345"
                disabled={submitting}
              />
            </label>
          </div>

          <div class="formRow">
            <label>
              Country <span class="required">*</span>
              <select
                value={this.state.country}
                onChange={(event) =>
                  this.setState({ country: event.currentTarget.value })
                }
                disabled={submitting}
              >
                <option value="">Select a country...</option>
                {COUNTRIES.map((c) => (
                  <option value={c.code}>{c.name}</option>
                ))}
              </select>
            </label>
          </div>

          <div class="formActions">
            <button
              class="primary"
              onClick={this.submitAddress}
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save Billing Address"}
            </button>
          </div>

          <div class="refuseOption">
            {isEditing ? (
              <button
                class="link"
                onClick={() => this.setState({ showAddressForm: false })}
                disabled={submitting}
              >
                Cancel
              </button>
            ) : (
              <button
                class="link"
                onClick={this.refuseAddress}
                disabled={submitting}
              >
                I prefer not to provide my address
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderAppStoreSubscription(
    accountState: Extract<AccountPremiumState, { type: "appStoreSubscription" }>
  ) {
    const inApp = deviceDetect.isIOSApp();
    return (
      <div>
        <h2>Your {tierName(accountState.plan)} Subscription</h2>
        <table class="table">
          <caption>
            Bought in the Readlang iPhone app, from the App Store.
            {inApp
              ? null
              : " Manage it on your iPhone, in Settings, under your Apple Account's Subscriptions."}
          </caption>
          <tr>
            <th>Plan</th>
            <td>
              {tierName(accountState.plan)}
              {accountState.months
                ? ` (${accountState.months === 12 ? "yearly" : "monthly"})`
                : null}
            </td>
          </tr>
          <tr>
            <th>
              {accountState.renewsAutomatically ? "Renews on" : "Access until"}
            </th>
            <td>{formatDate(accountState.expiresAt)}</td>
          </tr>
          {inApp ? (
            <tr>
              <th></th>
              <td>
                <button
                  onClick={async () => {
                    await appStorePurchases.manageSubscriptions();
                    this.load();
                  }}
                >
                  Manage Subscription
                </button>
              </td>
            </tr>
          ) : null}
        </table>
      </div>
    );
  }

  /**
   * In the iOS app, where Apple allows no other way of paying, a subscription
   * paid for on the website: what it is, without the website's controls
   */
  renderPaidOnWebsite(
    accountState: Extract<
      AccountPremiumState,
      { type: "accessGrant" | "stripeBridge" | "stripeSubscription" }
    >
  ) {
    const until =
      accountState.type === "accessGrant"
        ? accountState.endsAt
        : accountState.type === "stripeBridge"
          ? accountState.billingStartsAt
          : accountState.currentPeriodEnd;
    return (
      <div>
        <h2>Your {tierName(accountState.plan)} Account</h2>
        <p>
          {accountState.type === "accessGrant"
            ? `${accountState.sourceLabel} until ${formatDate(until)}.`
            : accountState.cancelAtPeriodEnd
              ? `Your subscription ends on ${formatDate(until)}.`
              : `Your subscription renews on ${formatDate(until)}.`}
        </p>
        {accountState.type === "accessGrant" ? null : (
          <p>It's managed on the Readlang website.</p>
        )}
      </div>
    );
  }

  render() {
    if (this.state.status === "loading") {
      return <p>Loading...</p>;
    }

    if (this.state.status === "error") {
      return (
        <p>
          Couldn't load your subscription details.{" "}
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              this.setState({ status: "loading" });
              this.load();
            }}
          >
            Try again
          </a>
        </p>
      );
    }

    const { accountState } = this.state;

    switch (accountState.type) {
      case "freeForever":
        return (
          <div>
            <h2>Manage your Premium Plus subscription</h2>
            <p>
              You have a free lifetime subscription to Premium Plus (which now
              costs $15 / month or $120 / year) for being one of the first 600
              users to sign up to Readlang back in 2013.
            </p>
          </div>
        );

      case "appStoreSubscription":
        return this.renderAppStoreSubscription(accountState);

      case "accessGrant":
      case "stripeBridge":
      case "stripeSubscription":
        if (deviceDetect.isIOSApp()) {
          return this.renderPaidOnWebsite(accountState);
        }
        return accountState.type === "accessGrant"
          ? this.renderAccessGrant(accountState)
          : accountState.type === "stripeBridge"
            ? this.renderBridge(accountState)
            : this.renderSubscription(accountState);

      default:
        return (
          <div>
            <h2>Your Premium account</h2>
            <p>
              You don't currently have premium access.{" "}
              <a href="/checkout">See premium plans</a>.
            </p>
          </div>
        );
    }
  }
}

export default ManageSubscriptionView;
